import { groupBy, omitBy, uniq } from 'lodash-es';
import type { ConnectedTypes } from '../../make-connected';
import { getDayIndex, parseDateInterval } from '../../../core/util';
import { idOf } from './util/util';
import { parseGroupReferences } from './util/parse-group-references';
import type { Types } from '../../types';
import { makeChainable } from '../../../common/make-chainable';
import { toDayAndStart } from './events';
import { getDefaultInterval } from './intervals';
import type { Interval } from '../../../core/interfaces';
import { parseLocationReferences } from './util/parse-location-references';

function parseRegular (
  lockedTime: ConnectedTypes.lockedTime,
  options:    Types.parsedToOptions,
): Types.Entities.BlockedTime | undefined {
  const { start: startDate, end: endDate } = parseDateInterval(lockedTime, 'locked time');

  const duration = endDate.diff(startDate, 'minutes');
  if (duration < 5) throw new Error('(RS::To::parseRegular) Length of a locked time is less than 5 min');

  const start = parseFloat(startDate.format('HH.mm'));
  const day   = getDayIndex(startDate);

  const doc: Types.Entities.BlockedTime = {
    id:       idOf.lockedTime(lockedTime, options),
    day:      day,
    start:    start,
    duration: duration,
  };

  if (options.includeEntityMeta) {
    doc.name = lockedTime.displayName;
  }

  return doc;
};

function parseDynamic (
  lockedTime:      ConnectedTypes.lockedTime,
  settings:        ConnectedTypes.divisionSettings,
  options:         Types.parsedToOptions,
  defaultInterval: Interval[]
) {
  const duration = lockedTime.duration;
  if (!duration || duration < 5) throw new Error('(RS::To::parseDynamic) Length of a locked time is less than 5 min');

  // Parse location dependencies directly from coalesced edges.
  const dependencyReferences = parseLocationReferences(lockedTime.coalesced, options);

  const days = lockedTime.intervals
    ? makeChainable(lockedTime.intervals)
      .chain(
        x => x.map(x => getDayIndex(x.start)),
        x => uniq(x),
      )
      .value
    : undefined;
  const day = days?.length == 1 ? days[0] : undefined;

  // ignore locked times that reside on days that are not part of any coalesced's days
  if (day != null && lockedTime.coalesced) {
    const allowedByEvery = lockedTime.coalesced.every(x => !x.to.days?.length || x.to.days.some(x => x.day == day));
    if (!allowedByEvery) return;
  }

  // each type (tag) of complementary hours should distribute evenly
  const distributionKey = lockedTime.type == 'COMPLEMENTARY_TIME'
    ? 'COMPLEMENTARY_TIME.' + lockedTime.tags?.at(0)?.value
    : undefined;

  const intervals = lockedTime.intervals ?? defaultInterval;

  const doc: Types.Entities.Event = {
    id:                  idOf.lockedTime(lockedTime, options),
    duration:            duration,
    minBreakDuration:    false,
    maxDurationVariance: lockedTime.durationVariance,
    groups:              parseGroupReferences({ type: 'lockedTime', item: lockedTime }, options),
    dependencies:        dependencyReferences,
    intervals:           idOf.intervalPairReference(intervals, undefined, options),
    distributionKey,
    ...days && { days },
    ...day != null && { day },
  };


  ////
  //// filter based on partialScheduleOptions
  ////
  if (options.partialScheduleOptions) {
    const { includedEvents, omittedEventsHandling } = options.partialScheduleOptions;
    if (includedEvents && !includedEvents.has(doc.id)) {

      if (omittedEventsHandling == 'ignore') return;

      if (omittedEventsHandling == 'freeze') {
        // must not be parked and have a start and duration to be frozen, otherwise it's ignored
        if (lockedTime.parked || !lockedTime.start || !lockedTime.duration) return;

        // fix day, start and end
        Object.assign(doc, toDayAndStart(lockedTime.start));
        doc.duration = lockedTime.duration;
        doc.maxDurationVariance = 0;

        // override intervals and days to not cause conflicts
        const numDays = settings.numDays ?? 5;
        doc.days      = Array.from({ length: numDays }, (_, i) => i);
        doc.intervals = Array.from({ length: numDays }, () => [{ start: 0, end: 24.00 }]);

        // fix locations
        // > locations are always fixed for locked times
      }
    }
  }

  return omitBy(doc, x => x == null) as Types.Entities.Event;
}

type Out = {
  events:       Types.Entities.Event[];
  activities:   Types.Entities.Activity[];
  blockedTimes: Types.Entities.BlockedTime[];
};
export function fromLockedTimes (
  lockedTimes: ConnectedTypes.lockedTime[],
  settings:    ConnectedTypes.divisionSettings,
  options:     Types.parsedToOptions,
): Out {
  const defaultInterval = getDefaultInterval(settings);

  const blockedTimes = lockedTimes
    .filter(x => x.type == null || x.type == 'ANY')
    .map(lockedTime => parseRegular(lockedTime, options))
    .filter(x => !!x) as Types.Entities.BlockedTime[];

  const complementaryHours = lockedTimes
    .filter(x => x.type == 'COMPLEMENTARY_TIME')
    .map(x => parseDynamic(x, settings, options, defaultInterval))
    .filter(x => !!x);

  const lunches = lockedTimes
    .filter(x => x.type == 'LUNCH')
    .map(lockedTime => parseDynamic(lockedTime, settings, options, defaultInterval))
    .filter(x => !!x);

  const events = [...complementaryHours, ...lunches];

  const activities = Object.entries(groupBy(complementaryHours, x => [x.distributionKey].flat().toSorted().join('|')))
    .map(([id, events]) => ({ id: `COMPLEMENTARY_TIME.${id}`, events: events.map(x => x.id) }));

  return { events, activities, blockedTimes };
};